// surfboard-stocklist.js — GENERATED BUNDLE (do not edit by hand).
// Concatenation of config.js + inquiry.js + arrivals.js + filters.js + render-shopify.js
// with import/export stripped. Loaded as <script type="module"> (isolated scope, no imports).
// Regenerate from those modules if any changes.
// config.js
// Public contact destinations per store grouping. Call + Email are the live
// inquiry channels (SMS/text is disabled for now). Numbers verified from the
// hsssurf.com store locator on 2026-06-03.
const STORE_CONTACTS = {
  PCH: {
    label: 'PCH',
    text:  '+17148414000',          // (unused while SMS is off) — PCH store line
    call:  '+17148414000',          // PCH / Main — 300 Pacific Coast Hwy
  },
  Warner: {
    label: 'Warner',
    text:  '+17148460181',          // (unused while SMS is off) — Warner store line
    call:  '+17148460181',          // Warner — 3801 Warner Ave
  },
};
const INQUIRY_EMAIL = 'info@hsssurf.com';

// prototype/src/inquiry.js

/** Human message pre-filled into every inquiry channel. */
function composeMessage(board) {
  const len = board.length ? ` ${board.length}` : '';
  const store = board.store ? ` at the ${board.store} store` : '';
  return `Hi HSS - is the ${board.shaper} ${board.model}${len} still available${store}?`;
}

function contactFor(board, contacts) {
  return (contacts && contacts[board.store]) || { text: '', call: '' };
}

/** sms: deep link with pre-filled body. Uses `?body=` (most broadly supported form). */
function buildSmsUrl(board, { contacts }) {
  const { text } = contactFor(board, contacts);
  const body = encodeURIComponent(composeMessage(board));
  return `sms:${text}?body=${body}`;
}

/** tel: link to the board's store. */
function buildTelUrl(board, { contacts }) {
  const { call } = contactFor(board, contacts);
  return `tel:${call}`;
}

/** mailto: fallback with subject + body. */
function buildMailtoUrl(board, { email }) {
  const subject = encodeURIComponent(`Board inquiry: ${board.shaper} ${board.model}`);
  const body = encodeURIComponent(composeMessage(board));
  return `mailto:${email}?subject=${subject}&body=${body}`;
}

/** Gmail web compose, pre-filled. */
function buildGmailUrl(board, { email }) {
  const su = encodeURIComponent(`Board inquiry: ${board.shaper} ${board.model}`);
  const body = encodeURIComponent(composeMessage(board));
  return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${su}&body=${body}`;
}

/** Outlook web compose, pre-filled. */
function buildOutlookUrl(board, { email }) {
  const subject = encodeURIComponent(`Board inquiry: ${board.shaper} ${board.model}`);
  const body = encodeURIComponent(composeMessage(board));
  return `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(email)}&subject=${subject}&body=${body}`;
}

// prototype/src/arrivals.js

const DAY_MS = 24 * 60 * 60 * 1000;

/** Whole days between a first_seen ISO date string (YYYY-MM-DD, or falsy) and `now`. Date-only UTC math. */
function daysSince(firstSeen, now) {
  if (!firstSeen) return Infinity;
  const seen = new Date(`${firstSeen}T00:00:00Z`);
  if (Number.isNaN(seen.getTime())) return Infinity;
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return Math.floor((today.getTime() - seen.getTime()) / DAY_MS);
}

/** True if the board first appeared within `windowDays` (inclusive). `now` is required (a Date). */
function isNew(board, { now, windowDays = 14 } = {}) {
  return daysSince(board.first_seen, now) <= windowDays;
}


/**
 * Boards for the New Arrivals strip: those first-seen within `windowDays`,
 * padded up to `min` with the most-recent remaining boards, capped at `max`.
 * Always sorted newest-first by first_seen. `now` is required (a Date).
 * Ties on first_seen preserve input order (sort is stable).
 */
function selectNewArrivals(boards, { now, windowDays = 14, min = 6, max = 20 } = {}) {
  const byRecency = [...boards].sort((a, b) => {
    const da = daysSince(a.first_seen, now);
    const db = daysSince(b.first_seen, now);
    return da - db; // smaller daysSince = newer = first
  });
  const within = byRecency.filter(b => daysSince(b.first_seen, now) <= windowDays);
  let result = within;
  if (result.length < min) {
    result = byRecency.slice(0, min);          // fewer than min in window; take top-min overall (within boards are already at front)
  }
  return result.slice(0, max);
}

// prototype/src/filters.js

/** Parse a surfboard length like `5'10"` (also `5'10.5"`, `6'`, `10'0"`) into total inches.
 *  Anchored: returns null for non-strings or anything that isn't a clean length. */
function lengthToInches(length) {
  if (typeof length !== 'string') return null;
  const m = length.match(/^\s*(\d+)\s*'\s*(\d+(?:\.\d+)?)?\s*"?\s*$/);
  if (!m) return null;
  const feet = Number(m[1]);
  const inches = m[2] ? Number(m[2]) : 0;
  return feet * 12 + inches;
}

const collator = new Intl.Collator('en', { sensitivity: 'base' });

/** Sort a copy of `boards` by the chosen key. `by` ∈ default|price-asc|price-desc|length-asc|length-desc|newest.
 *  Equal primary keys break ties by shaper then model (A–Z, case-insensitive). `now` is required for `default`. */
function sortBoards(boards, { by = 'default', now } = {}) {
  const arr = [...boards];
  const byShaperModel = (a, b) =>
    collator.compare(a.shaper || '', b.shaper || '') ||
    collator.compare(a.model || '', b.model || '');
  const lenOr = (board, fallback) => {
    const v = lengthToInches(board.length);
    return v === null ? fallback : v;
  };

  switch (by) {
    case 'price-asc':  return arr.sort((a, b) => ((a.price ?? Infinity) - (b.price ?? Infinity)) || byShaperModel(a, b));
    case 'price-desc': return arr.sort((a, b) => ((b.price ?? -Infinity) - (a.price ?? -Infinity)) || byShaperModel(a, b));
    case 'length-asc': return arr.sort((a, b) => (lenOr(a, Infinity) - lenOr(b, Infinity)) || byShaperModel(a, b));
    case 'length-desc':return arr.sort((a, b) => (lenOr(b, -Infinity) - lenOr(a, -Infinity)) || byShaperModel(a, b));
    case 'newest':     return arr.sort((a, b) => ((b.first_seen || '').localeCompare(a.first_seen || '')) || byShaperModel(a, b));
    case 'default':
    default:
      return arr.sort((a, b) => {
        const an = isNew(a, { now }) ? 0 : 1;
        const bn = isNew(b, { now }) ? 0 : 1;
        return an - bn || byShaperModel(a, b);
      });
  }
}

/**
 * Filter boards by any combination of criteria (AND semantics). All criteria optional.
 * criteria: { search, shaper, store, priceMin, priceMax, lengthMinIn, lengthMaxIn, finSystem, condition, newOnly, now }
 * When `newOnly` is true, `now` (a Date) is required.
 */
function applyFilters(boards, criteria = {}) {
  const {
    search, shaper, store, priceMin, priceMax,
    lengthMinIn, lengthMaxIn, finSystem, condition, newOnly, now,
  } = criteria;

  const q = search ? String(search).trim().toLowerCase() : '';

  return boards.filter(b => {
    if (q) {
      const hay = `${b.shaper || ''} ${b.model || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (shaper && b.shaper !== shaper) return false;
    if (store && b.store !== store) return false;
    if (finSystem && b.fin_system !== finSystem) return false;
    if (condition && (b.condition || 'new') !== condition) return false;
    if (priceMin != null && !(b.price >= priceMin)) return false;
    if (priceMax != null && !(b.price <= priceMax)) return false;
    if (lengthMinIn != null || lengthMaxIn != null) {
      const inches = lengthToInches(b.length);
      if (inches === null) return false;
      if (lengthMinIn != null && inches < lengthMinIn) return false;
      if (lengthMaxIn != null && inches > lengthMaxIn) return false;
    }
    if (newOnly && !isNew(b, { now })) return false;
    return true;
  });
}

/** Distinct sorted shaper names — for populating the Shaper filter dropdown. */
function shaperOptions(boards) {
  return [...new Set(boards.map(b => b.shaper).filter(Boolean))].sort(collator.compare);
}

// prototype/render.js

const EXCLUDED_RE = /\b(taylor|pai)\b/i;

const LENGTH_PRESETS = {
  '': {},
  'short': { lengthMaxIn: 75 },
  'mid':   { lengthMinIn: 76, lengthMaxIn: 95 },
  'long':  { lengthMinIn: 96 },
};
const PRICE_PRESETS = {
  '': {},
  'u750': { priceMax: 749 },
  '750-1000': { priceMin: 750, priceMax: 1000 },
  'o1000': { priceMin: 1001 },
};

const state = { all: [], now: new Date() };

function readCriteria() {
  const len = LENGTH_PRESETS[document.querySelector('#filter-length').value] || {};
  const price = PRICE_PRESETS[document.querySelector('#filter-price').value] || {};
  return {
    search: document.querySelector('#board-search').value,
    shaper: document.querySelector('#filter-shaper').value || undefined,
    store: document.querySelector('#filter-store').value || undefined,
    newOnly: document.querySelector('#filter-new-only').checked,
    now: state.now,
    ...len, ...price,
  };
}

function boardActions(board) {
  return {
    call: buildTelUrl(board, { contacts: STORE_CONTACTS }),
    email: buildMailtoUrl(board, { email: INQUIRY_EMAIL }),
    gmail: buildGmailUrl(board, { email: INQUIRY_EMAIL }),
    outlook: buildOutlookUrl(board, { email: INQUIRY_EMAIL }),
  };
}

function fmtPrice(p) { return typeof p === 'number' ? `$${p.toLocaleString('en-US')}` : ''; }
function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }
function safeHref(url) {
  // Only allow known inquiry schemes; anything else becomes a no-op.
  return /^(sms:|tel:|mailto:|https:)/.test(url) ? esc(url) : '#';
}

function renderArrivals() {
  const strip = document.querySelector('#arrivals-strip');
  const arrivals = selectNewArrivals(state.all, { now: state.now });
  strip.innerHTML = arrivals.map(b => {
    const a = boardActions(b);
    return `<article class="arrival-card">
      <span class="justin">Just in</span>
      <div class="brand">${esc(b.shaper)}</div>
      <div class="model">${esc(b.model)}</div>
      <div class="stats">${[b.length, b.fin_system].filter(Boolean).map(esc).join(' · ')}</div>
      <div class="arr-row"><span class="store-badge">${esc(b.store)}</span><span class="price">${fmtPrice(b.price)}</span></div>
      <details class="email-menu">
        <summary class="act-email">✉️ Email us</summary>
        <div class="email-opts">
          <div class="email-opts-head">
            <span class="email-opts-label">Email us about this board</span>
            <button type="button" class="email-close" aria-label="Close">✕</button>
          </div>
          <a class="email-opt" href="${safeHref(a.email)}"><span class="email-opt-glyph" aria-hidden="true">✉</span>Mail app</a>
          <a class="email-opt" href="${safeHref(a.gmail)}" target="_blank" rel="noopener"><span class="email-opt-glyph" aria-hidden="true">G</span>Gmail</a>
          <a class="email-opt" href="${safeHref(a.outlook)}" target="_blank" rel="noopener"><span class="email-opt-glyph" aria-hidden="true">O</span>Outlook</a>
        </div>
      </details>
    </article>`;
  }).join('');
}

function renderTable() {
  const tbody = document.querySelector('#board-tbody');
  const filtered = sortBoards(applyFilters(state.all, readCriteria()), { by: document.querySelector('#sort-by').value, now: state.now });
  document.querySelector('#board-count').textContent = `${filtered.length} boards`;
  tbody.innerHTML = filtered.map(b => {
    const a = boardActions(b);
    const newDot = isNew(b, { now: state.now }) ? ' <span class="newdot">● NEW</span>' : '';
    return `<tr>
      <td class="b-brand" data-label="Shaper">${esc(b.shaper)}</td>
      <td class="b-model" data-label="Model">${esc(b.model)}${newDot}</td>
      <td data-label="Length">${esc(b.length ?? '')}</td>
      <td data-label="Fin">${esc(b.fin_system ?? '—')}</td>
      <td data-label="Store"><span class="store-badge">${esc(b.store)}</span></td>
      <td class="b-price" data-label="Price">${fmtPrice(b.price)}</td>
      <td class="actions" data-label="Inquire">
        <a class="act-call" href="${safeHref(a.call)}">Call</a>
        <details class="email-menu">
          <summary class="act-email">Email</summary>
          <div class="email-opts">
            <div class="email-opts-head">
              <span class="email-opts-label">Email us about this board</span>
              <button type="button" class="email-close" aria-label="Close">✕</button>
            </div>
            <a class="email-opt" href="${safeHref(a.email)}"><span class="email-opt-glyph" aria-hidden="true">✉</span>Mail app</a>
            <a class="email-opt" href="${safeHref(a.gmail)}" target="_blank" rel="noopener"><span class="email-opt-glyph" aria-hidden="true">G</span>Gmail</a>
            <a class="email-opt" href="${safeHref(a.outlook)}" target="_blank" rel="noopener"><span class="email-opt-glyph" aria-hidden="true">O</span>Outlook</a>
          </div>
        </details>
      </td>
    </tr>`;
  }).join('');
}

function populateShaperFilter() {
  const sel = document.querySelector('#filter-shaper');
  for (const s of shaperOptions(state.all)) {
    const opt = document.createElement('option');
    opt.value = s; opt.textContent = s; sel.appendChild(opt);
  }
}

function wireEvents() {
  document.querySelector('#board-search').addEventListener('input', renderTable);
  ['#filter-shaper', '#filter-store', '#filter-length', '#filter-price', '#sort-by', '#filter-new-only']
    .forEach(sel => document.querySelector(sel).addEventListener('change', renderTable));
}

// One delegated listener set, added once: ✕ closes its own menu, an outside
// click closes any open menu, and Escape closes them all.
function wireEmailMenus() {
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('.email-close');
    if (closeBtn) { const d = closeBtn.closest('details'); if (d) d.open = false; return; }
    document.querySelectorAll('.email-menu[open]').forEach(d => { if (!d.contains(e.target)) d.open = false; });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') document.querySelectorAll('.email-menu[open]').forEach(d => d.open = false);
  });
}

async function init() {
  const root = document.querySelector('[data-boards-url]');
  const url = root ? root.getAttribute('data-boards-url') : './boards.json';
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.all = (data.boards || []).filter(b => !EXCLUDED_RE.test(b.model || ''));
    const gen = data.generated_at;
    const lu = document.querySelector('#last-updated');
    if (lu) {
      if (gen) {
        const d = new Date(String(gen).slice(0, 10) + 'T00:00:00');
        lu.textContent = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        lu.setAttribute('datetime', String(gen).slice(0, 10));
      } else {
        const wrap = lu.closest('.last-updated');
        if (wrap) wrap.hidden = true;   // no timestamp -> hide the line
      }
    }
    populateShaperFilter();
    wireEvents();
    wireEmailMenus();
    renderArrivals();
    renderTable();
    const pill = document.querySelector('#hdr-count-shadow');
    if (pill) pill.textContent = state.all.length;
  } catch (err) {
    const tbody = document.querySelector('#board-tbody');
    if (tbody) tbody.innerHTML =
      `<tr><td colspan="7" style="padding:2rem;text-align:center">Unable to load inventory right now — please refresh, or call the shop.</td></tr>`;
    console.error('Board data load failed:', err);
  }
}
init();
